import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Grid,
  Stack,
  Fab,
  Tooltip,
  Alert,
  Snackbar,
  useTheme,
  useMediaQuery,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
   Menu,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
  Add as AddIcon,
  Search as SearchIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Archive as ArchiveIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  LocationOn as LocationIcon,
  Business as BusinessIcon,
  PersonAdd as PersonAddIcon,
  Public as PublicIcon,
   MoreVert as MoreVertIcon,
} from "@mui/icons-material";
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import LanguageIcon from '@mui/icons-material/Language';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PhoneInput from "react-phone-input-2";
import 'react-phone-input-2/lib/style.css';
import * as XLSX from 'xlsx';
import { apiCall } from "../../../services/ApiCall.js";
import config from "../../../config/config.json";
import ViewPage from "./ViewPage.js";
import EditCompany from "./EditCompany.js";
import { CanWrite } from "../../../context/AuthContext.jsx"; 

const Main = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // State management
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 50,
  });

  // Modal states
  const [editData, setEditData] = useState(null);
  const [viewData, setViewData] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showAddContactModal, setShowAddContactModal] = useState(false);

  // Delete/Archive states
  const [deleteId, setDeleteId] = useState(null);
  const [deleteMode, setDeleteMode] = useState("DELETE");

  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  // Contact form states
  const [contacts, setContacts] = useState([{
    fname: "",
    lname: "",
    email: "",
    phone: "",
    designation: ""
  }]);
  const [contactErrors, setContactErrors] = useState({});
  // ADDED FOR DROP DOWN MENU
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRowId, setSelectedRowId] = useState(null);//TRACKS WHICH ROW'S MENU IS OPEN
  const open = Boolean(anchorEl);
//OPENS DROPDOWN MENU
  const handleMenuClick = (event, rowId) => {
    setAnchorEl(event.currentTarget);
    setSelectedRowId(rowId);
  };
//CLOSE DROPDOWN MENU
  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRowId(null);
  };

  // Theme configuration
  const primaryColor = "#0d6efd";

  const handleContactChange = (index, key, value) => {
    const updatedContacts = contacts.map((contact, i) =>
      i === index ? { ...contact, [key]: value } : contact
    );
    setContacts(updatedContacts);
  };

  const addNewContact = () => {
    setContacts([...contacts, { fname: "", lname: "", email: "", phone: "", designation: "" }]);
  };

  const removeContact = (index) => {
    const updatedContacts = contacts.filter((_, i) => i !== index);
    setContacts(updatedContacts);
  };

  const validateContacts = () => {
    const newErrors = {};
    contacts.forEach((contact, index) => {
      const fname = contact.fname || "";
      const email = contact.email || "";
      const designation = contact.designation || "";

      if (!fname.trim()) {
        newErrors[`fname-${index}`] = "First Name is required";
      }
      if (!email.trim() || !/\S+@\S+\.\S+/.test(email)) {
        newErrors[`email-${index}`] = "A valid Email is required";
      }
      if (!designation.trim()) {
        newErrors[`designation-${index}`] = "Designation is required";
      }
    });
    setContactErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const addNewCompanyAndContact = async (e) => {
    e.preventDefault();
    if (!validateContacts()) return;

    try {
      const { isSuccess } = await apiCall(
        `${config.POST_LOGIN_API_URL}addNewCompanyContact`,
        {},
        { contacts, companyId: Number(viewData.id) },
        "POST"
      );
      if (isSuccess) {
        getCompanyList();
        setShowAddContactModal(false);
        setContacts([{ fname: "", lname: "", email: "", phone: "", designation: "" }]);
        setSnackbar({ open: true, message: "Contact added successfully!", severity: "success" });
      }
    } catch (error) {
      setSnackbar({ open: true, message: "Failed to add contact", severity: "error" });
    }
  };

  const getCompanyList = async () => {
    setLoading(true);
    try {
      const { isSuccess, data: responseData } = await apiCall(
        `${config.POST_LOGIN_API_URL}getCompanyList`,
        {},
        {},
        "GET"
      );
      if (isSuccess) {
        setData(responseData?.data?.reverse() || []);
      }
    } catch (error) {
      setSnackbar({ open: true, message: "Failed to load companies", severity: "error" });
    }
    setLoading(false);
  };

  const handleAction = async () => {
    try {
      const endpoint = deleteMode === "DELETE" ? "deleteCompany" : "archiveCompany";
      const { isSuccess } = await apiCall(
        `${config.POST_LOGIN_API_URL}${endpoint}`,
        {},
        { companyId: deleteId },
        "POST"
      );
      if (isSuccess) {
        getCompanyList();
        setShowDeleteModal(false);
        setSnackbar({
          open: true,
          message: `Company ${deleteMode.toLowerCase()}d successfully!`,
          severity: "success"
        });
      }
    } catch (error) {
      setSnackbar({ open: true, message: "Action failed", severity: "error" });
    }
  };

  const exportToExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(
      data.map(company => ({
        'Company ID': company?.id,
        "ARA ID": company?.araId,
        "Company Name": company?.name,
        'Company Address': `${company?.street}, ${company?.city}, ${company?.state}, ${company?.zipcode}`,
        "Created Date": company?.createdAt ? new Date(company.createdAt).toLocaleDateString('en-US', {
          month: '2-digit',
          day: '2-digit',
          year: 'numeric',
        }).replace(/\//g, '-') : '',
      }))
    );

    worksheet['!cols'] = [
      { wch: 10 }, { wch: 15 }, { wch: 40 }, { wch: 50 }, { wch: 15 }
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Company");
    XLSX.writeFile(workbook, "Company.xlsx");
  };

  // DataGrid columns configuration
  const columns = [
    {
      field: 'id',
      headerName: 'ID',
      width: 80,
      headerAlign: 'center',
      align: 'center',
    },
    {
      field: 'araId',
      headerName: 'ARA ID',
      width: 120,
      headerAlign: 'center',
      align: 'center',
    },
    {
      field: 'name',
      headerName: 'Company Name',
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'start',
          height: '100%'
        }}>
          <BusinessIcon sx={{ color: primaryColor, fontSize: 20 }} />
          <Typography variant="body2" sx={{ fontWeight: 500 }} title={params.value}>
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'address',
      headerName: 'Address',
      flex: 1,
      minWidth: 250,
      valueGetter: (value, row) => {
        if (!row) return '';
        return `${row.street || ''}, ${row.city || ''}, ${row.state || ''}`;
      },
      renderCell: (params) => (
        <Box sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'start', gap: 1,
          height: '100%'
        }}>
          <LocationIcon sx={{ color: theme.palette.text.secondary, fontSize: 16 }} />
          <Typography variant="body2" color="text.secondary" title={params.value}>
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'state',
      headerName: 'State',
      width: 100,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          sx={{
            backgroundColor: `${'primaryColor'}15`,
            color: primaryColor,
            fontWeight: 500
          }}
        />
      ),
    },
    {/*
      field: 'actions',
      headerName: 'Actions',
      width: 300,
      headerAlign: 'center',
      align: 'center',
      sortable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={1} sx={{ height: '100%', alignItems: 'center' }}>
          <Tooltip title="View Details">
            <IconButton
              size="small"
              onClick={() => {
                setViewData(params.row);
                setShowViewModal(true);
              }}
              sx={{ color: primaryColor }}
            >
              <ViewIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title="Edit Company">
            <IconButton
              size="small"
              onClick={() => {
                setEditData(params.row);
                setShowEditModal(true);
              }}
              sx={{ color: theme.palette.warning.main }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title="Add Contact">
            <IconButton
              size="small"
              onClick={() => {
                setViewData(params.row);
                setShowAddContactModal(true);
              }}
              sx={{ color: theme.palette.success.main }}
            >
              <PersonAddIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title="Archive">
            <IconButton
              size="small"
              onClick={() => {
                setDeleteId(params.row.id);
                setDeleteMode("ARCHIVE");
                setShowDeleteModal(true);
              }}
              sx={{ color: theme.palette.grey[600] }}
            >
              <ArchiveIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Button
            size="small"
            variant="outlined"
            startIcon={<PublicIcon />}
            onClick={() => navigate(`/site-list/view/${params.row.id}`)}
            sx={{
              borderColor: primaryColor,
              color: primaryColor,
              fontSize: '0.75rem',
              px: 1,
              '&:hover': {
                backgroundColor: `${primaryColor}10`,
                borderColor: primaryColor,
              }
            }}
          >
            Sites
          </Button>
        </Stack>
      ),
    },
  ];*/}

,{
      field: 'actions',
      headerName: 'Actions',
      width: 80,
      headerAlign: 'center',
      align: 'center',
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <IconButton
            size="small"
            onClick={(event) => handleMenuClick(event, params.row.id)}
            sx={{ color: theme.palette.text.secondary }}
          >
            <MoreVertIcon />
          </IconButton>
        </Box>
      ),
    },
    // ================================================
  ];
  // Filter data based on search query
  const filteredData = data.filter((row) =>
    ["id", "araId", "name", "street", "city", "state", "zipcode"].some((key) =>
      row[key]?.toString().toLowerCase().includes(searchQuery.toLowerCase())
    )
  );

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/");
    } else {
      getCompanyList();
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/");
    } else if (!showEditModal) {
      getCompanyList();
    }
  }, [showEditModal]);

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: '100%', overflow: 'hidden' }}>
      {/* Header Section */}
      <Paper elevation={0} sx={{ p: 3, mb: 3, backgroundColor: 'transparent' }}>
        <Box sx={{
          display: 'flex',
          alignItems: { xs: 'flex-start', md: 'center' },
          justifyContent: 'space-between',
          mb: 3,
          flexDirection: { xs: 'column', md: 'row' },
          gap: { xs: 2, md: 0 }
        }}>
          <Box>
            <Typography variant="h4" sx={{
              fontWeight: 600,
              color: 'black',
              mb: 1,
              fontSize: { xs: '1.75rem', md: '2.125rem' }
            }}>
              Companies
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Manage your company directory
            </Typography>
          </Box>

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            sx={{
              width: { xs: '100%', md: 'auto' },
              mt: { xs: 1, md: 0 }
            }}
          >
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={exportToExcel}
              sx={{
                borderColor:'#a3a3a3ff' ,
                color: '#696868ff',
                width: { xs: '100%', sm: 'auto' },
                '&:hover': {
                  backgroundColor:`${'black'}10`,
                  borderColor:'#a3a3a3ff',
                }
              }}
            >
              Export
            </Button>

              <CanWrite>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate("/add-company")}
              sx={{
                backgroundColor: primaryColor,
                
                '&:hover': {
                  backgroundColor: primaryColor,
                  opacity: 0.9,
                }
              }}
            >
              Add Company
            </Button>
            </CanWrite>
          </Stack>
        </Box>


    {/* Rest of your content would continue here */}
  
        {/* Controls Row */}
        <Grid container spacing={2} alignItems="center">
          {/* Show Entries */}
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Show
              </Typography>
              <FormControl size="small" sx={{ minWidth: 70 }}>
                <Select
                  value={paginationModel.pageSize}
                  onChange={(e) => setPaginationModel({
                    ...paginationModel,
                    pageSize: Number(e.target.value),
                    page: 0
                  })}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1,
                    }
                  }}
                >
                  <MenuItem value={25}>25</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                  <MenuItem value={250}>250</MenuItem>
                  <MenuItem value={500}>500</MenuItem>
                </Select>
              </FormControl>
              <Typography variant="body2" color="text.secondary">
                entries
              </Typography>
            </Box>
          </Grid>

          {/* Search Box */}
          <Grid item xs={12} sm={12} md={6}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by ID, Name, Street, City, State, or Zipcode..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                }
              }}
            />
          </Grid>

          {/* Add Company Button */}

        </Grid>

        {/* Export and Results Info */}
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mt: 2,
          flexWrap: 'wrap',
          gap: 2
        }}>


          <Typography variant="body2" color="text.secondary">
            {filteredData.length} companies found
          </Typography>
        </Box>  
        
      </Paper>

      {/* Data Grid */}
      <Paper elevation={1} sx={{ height: 600, borderRadius: 2 ,display: { xs: 'none', md: 'block' }}}>
        <DataGrid
          rows={filteredData}
          columns={columns}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[25, 50, 100]}
          loading={loading}
          disableRowSelectionOnClick
          sx={{
            border: 'none',
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: `${primaryColor}08`,
              borderBottom: `2px solid ${primaryColor}20`,
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              fontWeight: 500,
              color:'rgba(49, 50, 51, 1)',
            },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: `${primaryColor}05`,
            },
            '& .MuiDataGrid-cell': {
              borderBottom: `1px solid ${theme.palette.divider}`,
            },
          }}
        />
      </Paper>
{/* ============ ADDED DROPDOWN MENU ============ */}
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        MenuListProps={{
          'aria-labelledby': 'basic-button',
        }}
        sx={{
          '& .MuiPaper-root': {
            borderRadius: 2,
            minWidth: 180,
            boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
          }
        }}
      >
        <MenuItem onClick={() => {
          const selectedRow = data.find(row => row.id === selectedRowId);
          setViewData(selectedRow);
          setShowViewModal(true);
          handleMenuClose();
        }}>
          <ListItemIcon>
            <ViewIcon fontSize="small" sx={{ color: primaryColor }} />
          </ListItemIcon>
          <ListItemText primary="View Details" />
        </MenuItem>

        <CanWrite>
        <MenuItem onClick={() => {
          const selectedRow = data.find(row => row.id === selectedRowId);
          setEditData(selectedRow);
          setShowEditModal(true);
          handleMenuClose();
        }}>
          <ListItemIcon>
            <EditIcon fontSize="small" sx={{ color: theme.palette.warning.main }} />
          </ListItemIcon>
          <ListItemText primary="Edit Company" />
        </MenuItem>
        </CanWrite>

        <CanWrite>
        <MenuItem onClick={() => {
          const selectedRow = data.find(row => row.id === selectedRowId);
          setViewData(selectedRow);
          setShowAddContactModal(true);
          handleMenuClose();
        }}>
          <ListItemIcon>
            <PersonAddIcon fontSize="small" sx={{ color: theme.palette.success.main }} />
          </ListItemIcon>
          <ListItemText primary="Add Contact" />
        </MenuItem>
        </CanWrite>

        <CanWrite>
        <MenuItem onClick={() => {
          setDeleteId(selectedRowId);
          setDeleteMode("ARCHIVE");
          setShowDeleteModal(true);
          handleMenuClose();
        }}>
          <ListItemIcon>
            <ArchiveIcon fontSize="small" sx={{ color: theme.palette.grey[600] }} />
          </ListItemIcon>
          <ListItemText primary="Archive" />
        </MenuItem>
        </CanWrite>

        <MenuItem onClick={() => {
          navigate(`/site-list/view/${selectedRowId}`);
          handleMenuClose();
        }}>
          <ListItemIcon>
            <PublicIcon fontSize="small" sx={{ color: primaryColor }} />
          </ListItemIcon>
          <ListItemText primary="View Sites" />
        </MenuItem>
      </Menu>
      {/* ============================================= */}

        <Box sx={{ display: { xs: 'block', md: 'none' } }}>
        {/* Loading State */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Mobile Cards */}
            {filteredData
              .slice(
                paginationModel.page * paginationModel.pageSize,
                (paginationModel.page + 1) * paginationModel.pageSize
              )
              .map((company) => (
                <Card 
                  key={company.id} 
                  sx={{ 
                    mb: 2, 
                    borderRadius: 2,
                    border: '1px solid #e0e0e0',
                    overflow: 'hidden'
                  }}
                >
                  {/* Card Header */}
                  <CardHeader
                    sx={{ 
                      backgroundColor: `${primaryColor}08`,
                      pb: 1,
                      pt: 2
                    }}
                    title={
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box>
                          <Typography variant="h6" sx={{ color: primaryColor, fontWeight: 600, fontSize: '16px' }}>
                            ID: {company?.id}
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '14px', mt: 0.5 }}>
                            {company?.name}
                          </Typography>
                        </Box>
                      </Box>
                    }
                  />

                  <CardContent sx={{ pt: 1, pb: 1 }}>
                    {/* Address Info */}
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                        <LocationOnIcon sx={{ color: 'text.secondary', fontSize: 16, mt: 0.2 }} />
                        <Box sx={{ flex: 1 }}>
                          {company?.street && (
                            <Typography variant="body2" sx={{ fontSize: '12px', mb: 0.5 }}>
                              {company.street}
                            </Typography>
                          )}
                          <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '12px' }}>
                            {[company?.city, company?.state, company?.zipcode].filter(Boolean).join(', ')}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>

                    {/* Contact Info */}
                    {(company?.phone || company?.email) && (
                      <Box sx={{ mb: 2 }}>
                        {company?.phone && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <PhoneIcon sx={{ color: 'text.secondary', fontSize: 16 }} />
                            <Typography variant="body2" sx={{ fontSize: '12px' }}>
                              {company.phone}
                            </Typography>
                          </Box>
                        )}
                        {company?.email && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <EmailIcon sx={{ color: 'text.secondary', fontSize: 16 }} />
                            <Typography variant="body2" sx={{ fontSize: '12px' }}>
                              {company.email}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    )}

                    {/* Additional Info */}
                    <Box sx={{ mb: 2 }}>
                      {company?.website && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <LanguageIcon sx={{ color: 'text.secondary', fontSize: 16 }} />
                          <Typography variant="body2" sx={{ fontSize: '12px' }}>
                            {company.website}
                          </Typography>
                        </Box>
                      )}
                      {company?.industry && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <BusinessIcon sx={{ color: 'text.secondary', fontSize: 16 }} />
                          <Typography variant="body2" sx={{ fontSize: '12px' }}>
                            {company.industry}
                          </Typography>
                        </Box>
                      )}
                    </Box>

                    {/* Actions */}
                    <Box sx={{ borderTop: '1px solid #f0f0f0', pt: 2 }}>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'center' }}>
                        {/* Render the same actions that are in your DataGrid columns */}
                        {columns.find(col => col.field === 'actions')?.renderCell({ row: company })}
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              ))}

            {/* Mobile Pagination */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              mt: 2, 
              gap: 2,
              flexWrap: 'wrap'
            }}>
              <IconButton
                disabled={paginationModel.page === 0}
                onClick={() => setPaginationModel({ 
                  ...paginationModel, 
                  page: paginationModel.page - 1 
                })}
                sx={{ 
                  border: '1px solid #e0e0e0',
                  borderRadius: 1
                }}
              >
                <ChevronLeftIcon/>
              </IconButton>

              <Typography variant="body2" sx={{ px: 2 }}>
                Page {paginationModel.page + 1} of {Math.ceil(filteredData.length / paginationModel.pageSize)}
              </Typography>

              <IconButton
                disabled={paginationModel.page >= Math.ceil(filteredData.length / paginationModel.pageSize) - 1}
                onClick={() => setPaginationModel({ 
                  ...paginationModel, 
                  page: paginationModel.page + 1 
                })}
                sx={{ 
                  border: '1px solid #e0e0e0',
                  borderRadius: 1
                }}
              >
                <ChevronRightIcon />
              </IconButton>
            </Box>

            {/* Mobile Results Info */}
            <Box sx={{ textAlign: 'center', mt: 2, mb: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Showing {Math.min((paginationModel.page + 1) * paginationModel.pageSize, filteredData.length)} of {filteredData.length} companies
              </Typography>
            </Box>
          </>
        )}
      </Box>
   

      {/* Delete/Archive Confirmation Dialog */}
      <Dialog open={showDeleteModal} onClose={() => setShowDeleteModal(false)}>
        <DialogTitle>
          {deleteMode === "DELETE" ? "Delete Confirmation" : "Archive Confirmation"}
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to {deleteMode === "DELETE" ? "delete" : "archive"} this company?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteModal(false)}>Cancel</Button>
          <Button
            onClick={handleAction}
            color={deleteMode === "DELETE" ? "error" : "warning"}
            variant="contained"
          >
            {deleteMode === "DELETE" ? "Delete" : "Archive"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Modal */}
      <Dialog open={showViewModal} onClose={() => setShowViewModal(false)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ color: primaryColor, fontSize: '1.5rem', fontWeight: 600 }}>
          Company Details
        </DialogTitle>
        <DialogContent>
          {viewData && <ViewPage viewData={viewData} />}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowViewModal(false)} sx={{ color: primaryColor }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={showEditModal} onClose={() => setShowEditModal(false)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ color: primaryColor, fontSize: '1.5rem', fontWeight: 600 }}>
          Edit Company
        </DialogTitle>
        <DialogContent>
          {editData && <EditCompany editData={editData} setShowDetails={setShowEditModal} />}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowEditModal(false)} sx={{ color: primaryColor }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Contact Modal */}
      <Dialog open={showAddContactModal} onClose={() => setShowAddContactModal(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ color: primaryColor, fontSize: '1.5rem', fontWeight: 600 }}>
          Add Company Contact
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {contacts.map((contact, index) => (
              <Card key={index} sx={{ mb: 3, border: `1px solid ${primaryColor}20` }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" sx={{ color: primaryColor }}>
                      Contact {index + 1}
                    </Typography>
                    <Stack direction="row" spacing={1}>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={addNewContact}
                        sx={{ borderColor: primaryColor, color: primaryColor }}
                      >
                        Add
                      </Button>
                      {contacts.length > 1 && (
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          startIcon={<DeleteIcon />}
                          onClick={() => removeContact(index)}
                        >
                          Remove
                        </Button>
                      )}
                    </Stack>
                  </Box>

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="First Name"
                        value={contact.fname}
                        onChange={(e) => handleContactChange(index, "fname", e.target.value)}
                        error={!!contactErrors[`fname-${index}`]}
                        helperText={contactErrors[`fname-${index}`]}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Last Name"
                        value={contact.lname}
                        onChange={(e) => handleContactChange(index, "lname", e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Email"
                        type="email"
                        value={contact.email}
                        onChange={(e) => handleContactChange(index, "email", e.target.value)}
                        error={!!contactErrors[`email-${index}`]}
                        helperText={contactErrors[`email-${index}`]}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box>
                        <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                          Phone
                        </Typography>
                        <PhoneInput
                          country={'us'}
                          value={contact.phone}
                          onChange={(phone) => handleContactChange(index, 'phone', phone)}
                          inputStyle={{
                            width: '100%',
                            height: '56px',
                            borderRadius: '8px',
                            border: '1px solid rgba(0, 0, 0, 0.23)',
                          }}
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <FormControl fullWidth error={!!contactErrors[`designation-${index}`]}>
                        <InputLabel>Designation</InputLabel>
                        <Select
                          value={contact.designation}
                          label="Designation"
                          onChange={(e) => handleContactChange(index, "designation", e.target.value)}
                        >
                          <MenuItem value="Owner">Owner</MenuItem>
                          <MenuItem value="District Manager">District Manager</MenuItem>
                          <MenuItem value="Compliance Manager">Compliance Manager</MenuItem>
                          <MenuItem value="General Manager">General Manager</MenuItem>
                        </Select>
                        {contactErrors[`designation-${index}`] && (
                          <Typography variant="caption" color="error" sx={{ ml: 2, mt: 0.5 }}>
                            {contactErrors[`designation-${index}`]}
                          </Typography>
                        )}
                      </FormControl>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAddContactModal(false)}>
            Cancel
          </Button>
          <Button
            onClick={addNewCompanyAndContact}
            variant="contained"
            sx={{ backgroundColor: primaryColor }}
          >
            Save Contacts
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Main;